'use client';

import React, { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export function PushNotificationManager() {
  const pathname = usePathname();
  const {
    isSupported,
    permission,
    needsHomeInstallation,
    requestPermissionAndSubscribe
  } = usePushNotifications();

  useEffect(() => {
    // Never prompt or trigger on admin routes
    if (pathname?.startsWith('/admin')) return;

    // If supported, permission is default (not yet asked), and not dismissed before
    const isDismissed = localStorage.getItem('push_notifications_dismissed') === 'true';
    
    // Only prompt if Web Push is supported and permission is not yet decided
    if (isSupported && permission === 'default' && !isDismissed && !needsHomeInstallation) {
      toast.custom(
        (t) => (
          <div className="flex w-full max-w-md items-center justify-between rounded-xl border border-border bg-card p-4 shadow-xl animate-in slide-in-from-bottom-5 duration-300 dark:bg-slate-900">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Bell className="h-5 w-5 animate-bounce" />
              </div>
              <div className="flex flex-col text-left">
                <span className="font-semibold text-sm text-foreground">Enable Push Notifications</span>
                <span className="text-xs text-muted-foreground">
                  Get real-time updates for KYC, deposits, and withdrawals.
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={async () => {
                  toast.dismiss(t);
                  const success = await requestPermissionAndSubscribe();
                  if (success) {
                    toast.success('Push notifications enabled successfully!');
                  } else {
                    toast.error('Failed to enable push notifications.');
                  }
                }}
              >
                Allow
              </Button>
              <button
                onClick={() => {
                  toast.dismiss(t);
                  localStorage.setItem('push_notifications_dismissed', 'true');
                }}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ),
        {
          duration: Infinity,
          position: 'bottom-right',
        }
      );
    }
  }, [isSupported, permission, needsHomeInstallation, requestPermissionAndSubscribe]);

  // Render nothing as this is a background banner manager
  return null;
}

